/* eslint-disable no-case-declarations */

import ContainerLarge from '~/components/molecules/ContainerLarge';
import { ContainerLargeType } from '../../components/molecules/ContainerLarge/ContainerLarge.helpers';
import Heading from '../../components/atoms/Heading';
import Image from '../../components/molecules/Image';
import Text from '../../components/molecules/Text';
import { isContainerLarge } from './renderer.helpers';
import CmsElmWrapper from '../../components/organisms/CmsElmWrapper';
import defineSpacing from '../defineSpacing';
import Video from '../../components/atoms/Video';
import Quote from '../../components/organisms/Quote';
import CtaLinks from '../../components/molecules/CtaLinks';
import Spacer from '../../components/molecules/Spacer';
import Hero from '../../components/organisms/Hero/Hero';
import { HeroSingleHighlight } from '../../components/organisms/Hero';
import { HeroContentHub } from '../../components/organisms/HeroContentHub';

import ContentHubTeaser from '../../components/organisms/ContentHubTeaser/ContentHubTeaser';
import CmsTeaserTiles from '../../components/organisms/CmsTeaserTiles/CmsTeaserTiles';
import { FactGrid } from '../../components/molecules/Fact/Fact';
import CmsTabs from '../../components/organisms/CmsTabs/CmsTabs';
import LinkList from '../../components/molecules/LinkList';
import Anchor from '../../components/atoms/Anchor';
import { sanitize } from 'isomorphic-dompurify';
import isLascheScenario from '../isLascheScenario';
import TeaserIntroMore from '../../components/molecules/TeaserIntroMore';
import PageHeading from '../../components/molecules/PageHeading';
import TeaserContact from '../../components/molecules/TeaserContact';
import mapTeaserEventProps from './mapping/mapTeaserEventProps';
import ApprenticeMatrixSearch from '../../components/molecules/ApprenticeMatrixSearch';
import QueryBuilder from '../../api/helpers/QueryBuilder';
import SocialStreamContainer from '../../components/molecules/SocialStreamContainer';

import GuidedSearchContainer from '../../components/molecules/GuidedSearchContainer';
import getHeadingTag from './helpers/getHeadingTag';
import convertTarget from './helpers/convertTarget';
import getLink from './helpers/getLink';
import getCtaLink from './helpers/getCtaLink';
import getSeparatorSpacing from './helpers/getSeparatorSpacing';
import parseRichtext from '../parseRichtext';
import Benefits from '../../components/organisms/Benefits/Benefits';
import ExternalReviews from '../../components/organisms/ExternalReviews/ExternalReviews';
import CmsJobTeaserOverview from '../../components/organisms/CmsJobTeaserOverview';
import TeaserFullWidth from '../../components/organisms/TeaserFullWidth';
import SpecialElement from '../../components/molecules/SpecialElement';
import Table from '../../components/atoms/Table/Table';
import { getClientConfig } from '../../config/getClientConfig';
import Slider from '../../components/molecules/Slider';
import EventCounter from '../../components/molecules/EventCounter';
import EventTeaser from '../../components/molecules/EventTeaser';
import EventOverviewSearch from '../../components/molecules/EventOverviewSearch';
import CmsTeaserCareer from '../../components/organisms/CmsTeaserCareer';

/**
 * Renders an array of CMS elements as React nodes.
 *
 * @param {ApiElements} content - The array of CMS elements to render.
 * @param {ApiContentContainerElement} [caller] - The parent container element, if any.
 * @param {ElementRenderOptions} [options] - Additional rendering options.
 * @param {ContainerLargeTypedef} [containerType] - The type of the current container.
 * @param {ContainerLargeTypedef} [parentContainerType] - The type of the parent container.
 * @param {number} [elmIndex] - The index of the current element in its container.
 * @param {boolean} [isPartOfSharedContent] - Whether the element is part of shared content.
 * @param {boolean} [isBrand] - Whether the brand style is applied.
 * @returns {Array<React.ReactNode>} The rendered React nodes.
 */
function renderElements(
  content,
  caller,
  options,
  containerType,
  parentContainerType,
  elmIndex,
  isPartOfSharedContent,
  isBrand,
) {
  const hasTopImage =
    content.length > 0
      ? content[0].type === 'HERO_STAGE_SINGLE_HIGHLIGHT'
      : false;
  /**
   * Returns the next element in the content array, skipping over 'ANCHOR' elements if present.
   *
   * @param {number} idx - The current index in the content array.
   * @returns {ApiElement|undefined} The next element, or undefined if at the end.
   */
  const getNextElement = (idx) => {
    if (idx < content.length - 1) {
      let nextElem = content[idx + 1];
      if (nextElem.type === 'ANCHOR' && idx < content.length - 2) {
        nextElem = content[idx + 2];
      }
      return nextElem;
    }
    return undefined;
  };

  /**
   * Renders a single CMS element as a React node.
   *
   * @param {any} element - The CMS element to render.
   * @param {number} idx - The index of the element in the content array.
   * @returns {React.ReactNode|null} The rendered React node or null.
   */
  const renderElement = (element, idx) => {
    let renderedElement;
    switch (element.type) {
      case 'CONTAINER_LARGE_A':
      case 'CONTAINER_LARGE_B':
      case 'CONTAINER_LARGE_C':
        /** @type {ContainerLargeApiProps} */
        const containerElement = element;

        renderedElement = renderContainer(
          containerElement,
          idx > 0 ? content[idx - 1] : undefined,
          getNextElement(idx),
          options,
        );
        break;
      case 'HEADING':
        /** @type {HeadingApiProps} */
        const headingElement = element;
        /**
         * @constant
         * @type {React.ComponentProps<typeof Heading>}
         */
        const headingProps = {
          headline: {
            tag: getHeadingTag(headingElement.level),
            text: headingElement.text,
          },
        };
        renderedElement = <Heading {...headingProps} />;
        break;
      case 'IMAGE':
        /** @type {ImageApiProps} */
        const imageElement = element;
        /**
         * @constant
         * @type {React.ComponentProps<typeof Image>}
         */
        const imageProps = {
          url: imageElement.image.url,
          alt: imageElement.image.alt,
          title: imageElement.image.title,
          caption: imageElement.caption,
          fullWidth: imageElement.image.fullWidth,
        };
        renderedElement = <Image {...imageProps} />;
        break;
      case 'VIDEO':
        /** @type {VideoApiProps} */
        const videoElement = element;
        const { provider, videoId, cloudName, poster, hash } = videoElement;

        /**
         * @constant
         * @type {React.ComponentProps<typeof Video>}
         */
        const videoProps = {
          provider,
          videoId,
          cloudName,
          poster,
          hash,
        };
        renderedElement = <Video {...videoProps} />;
        break;
      case 'CTA':
        /** @type {CtaApiProps} */
        const ctaElement = element;
        /**
         * @constant
         * @type {React.ComponentProps<typeof CtaLinks>}
         */
        const ctaProps = {
          align: ctaElement.alignment,
          ctas: ctaElement.items.map((ctaLink) => getCtaLink(ctaLink)),
          isBrand,
        };
        renderedElement = <CtaLinks {...ctaProps} />;
        break;
      case 'QUOTE':
        /** @type {QuoteApiProps} */
        const quoteElement = element;
        /**
         * @constant
         * @type {React.ComponentProps<typeof Quote>}
         */
        const quoteProps = {
          image: quoteElement.image,
          quote: quoteElement.quote,
          name: quoteElement.name,
          title: quoteElement.title,
        };
        if (quoteElement.link) {
          quoteProps.link = getLink(quoteElement.link);
        }
        renderedElement = <Quote {...quoteProps} />;
        break;
      case 'SEPARATOR':
        /** @type {SeparatorApiProps} */
        const separatorElement = element;
        /**
         * @constant
         * @type {React.ComponentProps<typeof Spacer>}
         */
        const separatorProps = {
          line: separatorElement.showLine,
          rooted: caller === undefined,
          spacing: getSeparatorSpacing(separatorElement.strength),
        };
        renderedElement = <Spacer {...separatorProps} />;
        break;
      case 'TEXT':
        /** @type {TextApiProps} */
        const textElement = element;
        const safeRichtext = sanitize(textElement.html ?? '', {
          ADD_ATTR: ['target'],
        });
        const updatedRichtext = parseRichtext(safeRichtext, options?.lang);

        /**
         * @constant
         * @type {React.ComponentProps<typeof Text>}
         */
        const textProps = {
          richtext: updatedRichtext,
          extendedGrid: caller && caller.type === 'CONTAINER_LARGE_A',
          isRootElement: caller === undefined,
          isPartOfSharedContent,
          isBrand,
        };
        renderedElement = <Text {...textProps} />;
        break;
      case 'HERO_STAGE':
        /** @type {HeroApiProps} */
        const heroStageElement = element;
        /**
         * @returns {React.ComponentProps<typeof Hero>}
         */
        const createHeroProps = () => {
          const generalProps = {
            headline: heroStageElement.headline,
            subHeadline: heroStageElement.subheadline,
            text: heroStageElement.text,
            cta: heroStageElement.cta
              ? getCtaLink(heroStageElement.cta)
              : undefined,
            mobileImage: heroStageElement.smallImage,
            orientation: heroStageElement.format,
            style: heroStageElement.style,
          };
          if (heroStageElement.video) {
            return {
              ...generalProps,
              video: {
                poster: heroStageElement.video.poster,
                files: {
                  webm: heroStageElement.video.formats.webm,
                  mp4: heroStageElement.video.formats.mp4,
                  ogv: heroStageElement.video.formats.ogg,
                },
              },
            };
          }
          return {
            ...generalProps,
            image: heroStageElement.image,
          };
        };

        /**
         * @constant
         * @type {React.ComponentProps<typeof Hero>}
         */
        const heroProps = createHeroProps();
        renderedElement = <Hero {...heroProps} />;
        break;
      case 'CONTENT_HUB_HERO':
        /** @type {HeroContentHubApiProps} */
        const contentHubHeroElement = element;
        /**
         * @constant
         * @type {React.ComponentProps<typeof HeroContentHub>}
         */
        const HeroContentHubProps = {
          id: contentHubHeroElement.id ?? undefined,
          image: contentHubHeroElement.image,
          mediaType: contentHubHeroElement.mediaType ?? undefined,
          media: contentHubHeroElement.media ?? undefined,
          title: contentHubHeroElement.title ?? undefined,
          description: contentHubHeroElement.description ?? undefined,
          topic: contentHubHeroElement.topic ?? undefined,
          postType: contentHubHeroElement.postType ?? undefined,
          date: contentHubHeroElement.date ?? undefined,
          url: contentHubHeroElement.url ?? undefined,
          topicPageUrl: contentHubHeroElement.topicPageUrl ?? undefined,
          isContentHubCategoryPage: options?.isContentHubCategoryPage,
        };
        renderedElement = <HeroContentHub {...HeroContentHubProps} />;
        break;
      case 'HERO_STAGE_SINGLE_HIGHLIGHT':
        /** @type {HeroSingleHighlightApiProps} */
        const heroStageSingleHightlightElement = element;
        /**
         * @constant
         * @type {React.ComponentProps<typeof HeroSingleHighlight>}
         */
        const heroSingleHighlightProps = {
          id: heroStageSingleHightlightElement.id ?? undefined,
          image: heroStageSingleHightlightElement.image,
          smallImage: heroStageSingleHightlightElement.smallImage,
          format: heroStageSingleHightlightElement.format ?? undefined,
          lines: heroStageSingleHightlightElement.lines ?? undefined,
        };
        renderedElement = <HeroSingleHighlight {...heroSingleHighlightProps} />;
        break;
      case 'CONTENT_HUB_TEASER':
        /** @type {ContentHubTeaserApiProps} */
        const contentHubTeaserElement = element;
        renderedElement = (
          <ContentHubTeaser
            id={contentHubTeaserElement.id}
            type={contentHubTeaserElement.type}
            style={contentHubTeaserElement.style}
            items={contentHubTeaserElement.items}
            language={options?.lang}
            isContentHubCategoryPage={options?.isContentHubCategoryPage}
          />
        );
        break;
      case 'CONTAINER_TEASER_RICH_TEXT':
        /** @type {TeaserTilesApiProps} */
        const containerTeaserRichtTextElement = element;
        /**
         * @constant
         * @type {React.ComponentProps<typeof CmsTeaserTiles>}
         */
        const teaserTileProps = {
          type: containerTeaserRichtTextElement.teaserSize,
          parentContainerType,
          tiles: containerTeaserRichtTextElement.teasers.map((teaser) => {
            let updatedTeaserRichtext;
            if (teaser.textV2) {
              const safeTeaserRichtext = sanitize(teaser.textV2 ?? '', {
                ADD_ATTR: ['target'],
              });
              updatedTeaserRichtext = parseRichtext(
                safeTeaserRichtext,
                options?.lang,
              );
            }
            return {
              ...teaser,
              textV2: updatedTeaserRichtext,
              mainLink: teaser.mainLink
                ? {
                    ...teaser.mainLink,
                    target: convertTarget(teaser.mainLink.target),
                  }
                : undefined,
              linkList: teaser.linkList
                ? teaser.linkList.map((link) => {
                    return {
                      ...link,
                      target: convertTarget(link.target),
                    };
                  })
                : undefined,
            };
          }),
        };
        renderedElement = <CmsTeaserTiles {...teaserTileProps} />;
        break;
      case 'FACTS':
        /** @type {FactsApiProps} */
        const factsElement = element;
        /**
         * @constant
         * @type {React.ComponentProps<typeof FactGrid>}
         */
        const factGridProps = {
          facts: factsElement.facts.map((apiFact) => ({
            ...apiFact,
            link: apiFact.link ? getLink(apiFact.link) : undefined,
          })),
        };
        renderedElement = <FactGrid {...factGridProps} />;
        break;
      case 'CONTAINER_TABS':
        /** @type {TabsApiProps} */
        const containerTabsElement = element;
        /**
         * @constant
         * @type {React.ComponentProps<typeof CmsTabs>}
         */
        const tabsProps = {
          id: containerTabsElement.id,
          items: containerTabsElement.tabs.map((cmsTab) => ({
            description: cmsTab.title,
            column: renderElements(
              cmsTab.content,
              containerTabsElement,
              options,
            ),
          })),
          isAccordion: containerTabsElement.format === 'ACCORDION',
        };
        renderedElement = <CmsTabs {...tabsProps} />;
        break;
      case 'LINK_LIST':
        /** @type {LinkListApiProps} */
        const linkListElement = element;
        /**
         * @constant
         * @type {React.ComponentProps<typeof LinkList>}
         */
        const linkListProps = {
          extendedGrid: caller && caller.type === 'CONTAINER_LARGE_A',
          links: linkListElement.links.map((cmsLink) => ({
            icon: cmsLink.icon,
            ...getLink(cmsLink.link),
          })),
          isBrand,
        };
        renderedElement = <LinkList {...linkListProps} />;
        break;
      case 'ANCHOR':
        /** @type {AnchorApiProps} */
        const anchorElement = element;
        /**
         * @constant
         * @type {React.ComponentProps<typeof Anchor>}
         */
        const anchorProps = {
          anchorName: anchorElement.name,
        };
        renderedElement = <Anchor {...anchorProps} />;
        break;
      case 'TABLE':
        /** @type {TableApiProps} */
        const tableElement = element;
        renderedElement = (
          <Table
            tableHead={tableElement.header}
            tableBody={tableElement.rows}
            dataId="table"
          />
        );
        break;
      case 'CAROUSEL':
        /** @type {CarouselApiProps} */
        const carouselElement = element;
        /**
         * @constant
         * @type {React.ComponentProps<typeof Slider>}
         */
        const carouselProps = {
          content: carouselElement.items.map((item) => ({
            src: item.image.url,
            alt: item.image.alt,
            text: item.caption,
          })),
        };
        renderedElement = <Slider {...carouselProps} />;
        break;
      case 'TEASER_INTRO_MORE':
        /** @type {TeaserIntroMoreApiProps} */
        const teaserIntroMoreElement = element;
        const teaserIntroContent = renderElements(
          teaserIntroMoreElement.content,
          teaserIntroMoreElement,
          options,
        );
        renderedElement = (
          <TeaserIntroMore>{teaserIntroContent}</TeaserIntroMore>
        );
        break;
      case 'TEASER_EVENT_COUNTER':
        /** @type {EventCounterApiProps} */
        const teaserEventCounterElement = element;
        /**
         * @constant
         * @type {React.ComponentProps<typeof CmsEventCounter>}
         */
        const eventCounterProps = {
          startTimeStamp: teaserEventCounterElement.eventStart,
          endTimeStamp: teaserEventCounterElement.eventEnd,
          textBeforeEvent: teaserEventCounterElement.textBeforeEvent,
          textDuringEvent: teaserEventCounterElement.textDuringEvent,
          linkBeforeEvent: getLink(teaserEventCounterElement.linkBeforeEvent),
          linkDuringEvent: getLink(teaserEventCounterElement.linkDuringEvent),
          icon: teaserEventCounterElement.icon,
        };
        renderedElement = <EventCounter {...eventCounterProps} />;
        break;
      case 'HEADING_H1':
        /** @type {PageHeadingApiProps} */
        const headingH1Element = element;
        /**
         * @constant
         * @type {React.ComponentProps<typeof PageHeading>}
         */
        const pageHeadingProps = {
          headlineText: headingH1Element.headline,
          bodyText: headingH1Element.subheadline,
          hasTopImage,
        };
        renderedElement = <PageHeading {...pageHeadingProps} />;
        break;
      case 'TEASER_CONTACT':
        /** @type {ContactTeaserApiProps} */
        const teaserContactElement = element;
        renderedElement = (
          <TeaserContact
            {...teaserContactElement.contact}
            containerType={containerType}
            elmIndex={elmIndex}
            isBrand={isBrand}
          />
        );
        break;
      case 'TEASER_EVENT':
        renderedElement = <EventTeaser {...mapTeaserEventProps(element)} />;
        break;
      case 'TEASER_CAREER':
        /** @type {TeaserCareerApiProps} */
        const teaserCareerElement = element;
        /**
         * Number of columns for the teaser grid.
         * @typedef {1 | 2 | 3} TeaserGrid
         */

        /**
         * @constant
         * @type {{ [key: string]: TeaserGrid }}
         */
        const TeaserCareerGridMap = {
          LARGE: 1,
          MEDIUM: 2,
          SMALL: 3,
        };

        /**
         * Converts an API teaser career item to TeaserCareerProps.
         *
         * @param {TeaserCareerItemApiProps} apiItem - The API item to convert.
         * @returns {TeaserCareerProps} The converted teaser career props.
         */
        const createTeaserCareerItem = (apiItem) => {
          const { headline, text, link } = apiItem;
          return {
            bgColor: apiItem.style,
            iconUrl: apiItem.image,
            headline,
            text,
            link,
          };
        };

        /**
         * @constant
         * @type {CmsTeaserCareerPropsType}
         */
        const teaserCareerProps = {
          grid: TeaserCareerGridMap[teaserCareerElement.itemSize],
          mainLink: teaserCareerElement.mainLink,
          teasers: teaserCareerElement.items.map((item) =>
            createTeaserCareerItem(item),
          ),
        };
        renderedElement = <CmsTeaserCareer {...teaserCareerProps} />;
        break;
      case 'EVENT_SEARCH':
        /** @type {EventOverviewApiProps} */
        const eventSearchElement = element;
        /**
         * @constant
         * @type {EventOverviewSearchProps}
         */
        const eventOverviewSearchProps = {
          searchId: eventSearchElement.searchId,
          targetGroupId: eventSearchElement.targetGroupId,
          locationId: eventSearchElement.locationId,
          queryBuilder: QueryBuilder,
          language: options?.lang,
        };
        renderedElement = <EventOverviewSearch {...eventOverviewSearchProps} />;
        break;
      case 'APPRENTICESHIP_MATRIX':
        /** @type {ApprenticeMatrixApiProps} */
        const apprenticeshipMatrixElement = element;
        /**
         * @constant
         * @type {ApprenticeMatrixSearchProps}
         */
        const apprenticeMatrixSearchProps = {
          searchId: apprenticeshipMatrixElement.searchId,
          groupId: apprenticeshipMatrixElement.groupId,
          subGroupId: apprenticeshipMatrixElement.subGroupId,
          locationId: apprenticeshipMatrixElement.locationId,
          isBrand,
        };
        renderedElement = (
          <ApprenticeMatrixSearch {...apprenticeMatrixSearchProps} />
        );
        break;
      case 'SOCIAL_STREAM':
        /** @type {SocialStreamApiProps} */
        const socialStreamApiElement = element;
        renderedElement = (
          <SocialStreamContainer
            streamId={socialStreamApiElement.socialStreamId}
            language={options?.lang}
          />
        );
        break;
      case 'GUIDED_SEARCH':
        /** @type {GuidedSearchApiProps} */
        const guidedSearchElement = element;
        /**
         * @constant
         * @type {React.ComponentProps<typeof GuidedSearchContainer>}
         */
        const guidedSearchProps = {
          formType: guidedSearchElement.formType,
          rootNodeId: guidedSearchElement.rootNodeId,
          rootNodeType: guidedSearchElement.rootNodeType,
          parentContainerType: containerType,
          language: options?.lang,
        };
        renderedElement = (
          <GuidedSearchContainer {...guidedSearchProps} isBrand={isBrand} />
        );
        break;
      case 'BENEFITS':
        /** @type {BenefitsApiProps} */
        const benefitsElement = element;
        /**
         * @constant
         * @type {React.ComponentProps<typeof Benefits>}
         */
        const benefitsProps = {
          items: benefitsElement.items,
          title: benefitsElement.title,
        };
        renderedElement = <Benefits {...benefitsProps} />;
        break;
      case 'EXTERNAL_REVIEWS':
        /** @type {ExternalReviewApiProps} */
        const externalReviewsElement = element;
        /**
         * @constant
         * @type {React.ComponentProps<typeof ExternalReviews>}
         */
        const externalReviewProps = {
          text: externalReviewsElement.text,
          image: externalReviewsElement.image,
          externalImage: externalReviewsElement.externalImage,
          link:
            externalReviewsElement.link && getLink(externalReviewsElement.link),
        };
        renderedElement = <ExternalReviews {...externalReviewProps} />;
        break;
      case 'OVERVIEW_TEASER_JOB':
        /** @type {OverviewTeaserJobApiProps} */
        const overviewTeaserJobElement = element;
        /**
         * @constant
         * @type {React.ComponentProps<typeof CmsJobTeaserOverview>}
         */
        const jobTeaserOverview = {
          config: overviewTeaserJobElement.config,
          format: overviewTeaserJobElement.format,
          language: options?.lang,
          landingPageCode: options?.landingPageCode,
          isBrand,
        };
        renderedElement = <CmsJobTeaserOverview {...jobTeaserOverview} />;
        break;
      case 'TEASER_FULL_WIDTH':
        /** @type {TeaserFullWidthProps} */
        const teaserFullWidthElement = element;
        renderedElement = <TeaserFullWidth {...teaserFullWidthElement} />;
        break;
      case 'SPECIAL_ELEMENT':
        /** @type {SpecialElementProps} */
        const specialElementElement = element;
        renderedElement = (
          <SpecialElement value={specialElementElement.value} />
        );
        break;
      default:
        return null;
    }

    return renderedElement;
  };

  const { theme } = getClientConfig();

  /**
   * Maps and renders each CMS element in the content array.
   *
   * @param {ApiElement} element - The CMS element to render.
   * @param {number} idx - The index of the element in the content array.
   * @returns {React.ReactNode|null} The rendered React node or null.
   */
  return content.map((element, idx) => {
    /**
     * @constant
     * @type {SpacingType}
     */
    const spacing = defineSpacing(
      theme,
      element,
      content[idx + 1] && content[idx + 1].type === 'ANCHOR'
        ? content[idx + 2]
        : content[idx + 1],
    );

    const editBtnText =
      element && element.editModeLink
        ? element.editModeLink.text || `Edit ${element.type}`
        : null;

    const editLink =
      element && element.editModeLink && options && options.cmsBaseUrl
        ? options.cmsBaseUrl + element.editModeLink.url
        : null;

    const editMode =
      editLink && editBtnText ? { text: editBtnText, link: editLink } : null;

    let declaredElement;
    let declarationFailed = false;

    try {
      declaredElement = renderElement(element, idx);
    } catch (e) {
      declaredElement = // TODO: make a proper Error Component instead (?)
        (
          <div
            style={{
              backgroundColor: 'red',
              padding: '50px',
              textAlign: 'center',
              color: 'white',
              width: '100%',
            }}
          >
            Error ({element.type})
          </div>
        );
      declarationFailed = true;
    }

    // Hide element with faulty data mapping in view mode
    if (declarationFailed && !(options && options.isEditMode === true)) {
      return null;
    }

    return (
      <CmsElmWrapper
        id={element.id || `elm_${element.type}`}
        key={idx}
        spacing={spacing}
        elementName={element.type}
        hasFadeIn={element.fade}
        editMode={editMode}
      >
        {declaredElement}
      </CmsElmWrapper>
    );
  });
}

export default renderElements;

/**
 * Renders a ContainerLarge CMS element as a React node.
 *
 * @param {any} element - The container element to render.
 * @param {any} [previousElement] - The previous element in the content array.
 * @param {any} [nextElement] - The next element in the content array.
 * @param {ElementRenderOptions} [options] - Additional rendering options.
 * @returns {React.ReactNode} The rendered ContainerLarge element.
 */
export function renderContainer(
  element,
  previousElement,
  nextElement,
  options,
) {
  let type;
  let elements;

  /**
   * @constant
   * @type {ContainerLargeTypedef}
   */
  let containerType;

  /**
   * @constant
   * @type {ContainerLargeTypedef}
   */
  let parentContainerType;
  const isBrand = element.style === 'BRAND';
  if (element.type === 'CONTAINER_LARGE_A') {
    /** @type {ContainerLargeAApiProps} */
    const containerLargeAElement = element;
    type = ContainerLargeType.A;
    parentContainerType = ContainerLargeType.A;
    elements = [
      renderElements(
        containerLargeAElement.column,
        containerLargeAElement,
        options,
        undefined,
        undefined,
        undefined,
        undefined,
        isBrand,
      ),
    ];
  } else {
    if (element.type === 'CONTAINER_LARGE_B') {
      type =
        element.format === 'NARROW_WIDE'
          ? ContainerLargeType.B1
          : ContainerLargeType.B2;
      containerType = type;
      parentContainerType = type;
    } else {
      type = ContainerLargeType.C;
      containerType = type;
      parentContainerType = type;
    }
    elements = element.columns.map(
      /**
       * @param {ApiElements} colElements
       * @param {number} index
       */
      (colElements, index) =>
        renderElements(
          colElements,
          element,
          options,
          containerType,
          parentContainerType,
          index,
          undefined,
          isBrand,
        ),
    );
  }

  /**
   * @type {ContainerLargeBgColorType | undefined}
   */
  let color;
  switch (element.style) {
    case 'BRAND':
      color = 'Brand';
      break;
    case 'GRAY':
      color = 'Grey';
      break;
    case 'NONE':
    default:
  }

  /**
   * @type {ContainerLargePaddingOrientation | undefined}
   */
  let paddingOrientation;

  const withoutPaddingBottom =
    element.style === 'NONE' &&
    nextElement &&
    ((isContainerLarge(nextElement) && nextElement.style === 'NONE') ||
      nextElement.type === 'SEPARATOR');

  const withoutPaddingTop =
    (element.style === 'NONE' &&
      previousElement &&
      previousElement.type === 'SEPARATOR') ||
    (element.type === 'CONTAINER_LARGE_A' &&
      previousElement &&
      previousElement.type === 'HEADING_H1');

  if (withoutPaddingBottom && withoutPaddingTop) {
    paddingOrientation = 'none';
  } else if (withoutPaddingBottom) {
    paddingOrientation = 'ptop';
  } else if (withoutPaddingTop) {
    paddingOrientation = 'pbottom';
  } else if (
    (nextElement && isLascheScenario(element, nextElement)) ||
    element.hasLasche
  ) {
    paddingOrientation = 'py-bottom-doubled';
  }

  return (
    <ContainerLarge
      type={type}
      elements={elements}
      bgColor={color}
      paddingOrientation={paddingOrientation}
    />
  );
}
